import React, { useEffect, useState } from "react";
import { X, Minimize2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useContent } from "@/lib/content";
import VideoEmbed from "./VideoEmbed";

/**
 * Persistent floating video that appears after N seconds of navigation.
 * Configurable via home_sections.floating_video_* fields in CMS.
 */
export default function FloatingVideo() {
  const { home_sections = {} } = useContent();
  const cfg = {
    enabled: home_sections.floating_video_enabled === true,
    url: home_sections.floating_video_url || "",
    delay_sec: Number(home_sections.floating_video_delay_sec || 15),
    title: home_sections.floating_video_title || "",
    hide_paths: (home_sections.floating_video_hide_paths || "").split(",").map((s) => s.trim()).filter(Boolean),
  };
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!cfg.enabled || !cfg.url) return;
    if (sessionStorage.getItem("places:floating-video:dismissed") === "1") return;
    const timer = setTimeout(() => setShow(true), cfg.delay_sec * 1000);
    return () => clearTimeout(timer);
  }, [cfg.enabled, cfg.url, cfg.delay_sec]);

  if (!cfg.enabled || !cfg.url || dismissed || !show) return null;
  if (cfg.hide_paths.some((p) => p && pathname.startsWith(p))) return null;

  return (
    <div className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-40 w-[260px] md:w-[320px] shadow-2xl rounded-lg overflow-hidden bg-black text-white ring-1 ring-white/10 animate-in slide-in-from-right-8" data-testid="floating-video">
      <div className="flex items-center justify-between px-3 py-1.5 bg-black/80 text-[10px] uppercase tracking-widest">
        <span className="truncate opacity-80">{cfg.title || "Assista"}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized((m) => !m)} className="p-1 hover:bg-white/10 rounded" aria-label="Minimizar" data-testid="floating-video-minimize">
            <Minimize2 className="w-3 h-3" />
          </button>
          <button
            onClick={() => { setDismissed(true); try { sessionStorage.setItem("places:floating-video:dismissed", "1"); } catch {} }}
            className="p-1 hover:bg-white/10 rounded"
            aria-label="Fechar"
            data-testid="floating-video-close"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {!minimized && <VideoEmbed src={cfg.url} autoplay loop title={cfg.title} aspect="aspect-video" />}
    </div>
  );
}
